import { createContext, useContext, useState, ReactNode } from 'react';

interface ContractScannerSettings {
  feeCollectorAddress: string;
  scanFee: string; // in BNB
}

interface ContractScannerSettingsContextType {
  settings: ContractScannerSettings;
  updateSettings: (newSettings: Partial<ContractScannerSettings>) => void;
}

const DEFAULT_SCANNER_SETTINGS: ContractScannerSettings = {
  feeCollectorAddress: "0x760A0D5cC0ca242eB83609C627AB00Ac3F726Ba7",
  scanFee: "0.05", // 0.05 BNB
};

const ContractScannerSettingsContext = createContext<ContractScannerSettingsContextType | undefined>(undefined);

export function ContractScannerSettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<ContractScannerSettings>(() => {
    try {
      const savedSettings = localStorage.getItem('contractScannerSettings');
      if (savedSettings) {
        const parsedSettings = JSON.parse(savedSettings);
        // Validate the structure of saved settings
        if (parsedSettings.feeCollectorAddress && 
            typeof parsedSettings.scanFee === 'string') {
          return parsedSettings;
        }
      }
    } catch (error) {
      console.error('Error loading contract scanner settings:', error);
    }
    return DEFAULT_SCANNER_SETTINGS;
  });

  const updateSettings = (newSettings: Partial<ContractScannerSettings>) => {
    setSettings(prevSettings => {
      const updatedSettings = { 
        ...prevSettings,
        ...newSettings,
      };
      try {
        localStorage.setItem('contractScannerSettings', JSON.stringify(updatedSettings));
      } catch (error) {
        console.error('Error saving contract scanner settings:', error);
      }
      return updatedSettings;
    });
  };

  return (
    <ContractScannerSettingsContext.Provider value={{ settings, updateSettings }}>
      {children}
    </ContractScannerSettingsContext.Provider>
  );
}

export function useContractScannerSettings() {
  const context = useContext(ContractScannerSettingsContext);
  if (context === undefined) {
    throw new Error('useContractScannerSettings must be used within a ContractScannerSettingsProvider');
  }
  return context;
}
